import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import { ModelInfoCacheService } from '../services/intellijetnet/model-info-cache.service'
import { useAuth } from '../contexts/AuthContext'
import { useProfile } from '../hooks/useProfile'
import { capitalizeMakeName, formatModelName } from '../utils/formatters'
import { supabase } from '../lib/supabase'
import OptionsSection from './market-configure/OptionsSection'
import EMPSection from './market-configure/EMPSection'
import MarketSettingsSection from './market-configure/MarketSettingsSection'
import CustomColumnsSection from './market-configure/CustomColumnsSection'

export default function MarketConfigure() {
  const { modelId } = useParams<{ modelId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { profile } = useProfile()
  const [modelInfo, setModelInfo] = useState<{
    model_id: number
    make_name: string
    model_name: string
    display_name: string
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasAccess, setHasAccess] = useState(false)
  const [checkingAccess, setCheckingAccess] = useState(true)

  useEffect(() => {
    if (modelId && user && profile) {
      checkAccess()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, user, profile])

  const checkAccess = async () => {
    try {
      setCheckingAccess(true)

      // Check if user is admin or global_admin
      if (profile?.account_type === 'admin' || profile?.account_type === 'global_admin') {
        setHasAccess(true)
        fetchModelData()
        return
      }

      // Check if employee user has access to this market
      if (profile?.account_type === 'employee' && modelId) {
        const { data: marketAssignment } = await supabase
          .from('market_assignments')
          .select('id')
          .eq('user_id', user!.id)
          .eq('model_id', parseInt(modelId, 10))
          .single()

        if (marketAssignment) {
          setHasAccess(true)
          fetchModelData()
        } else {
          setHasAccess(false)
          setError('You do not have access to configure this market')
        }
      } else {
        setHasAccess(false)
        setError('You do not have permission to access this page')
      }
    } catch (err: any) {
      setError('Failed to verify access permissions')
    } finally {
      setCheckingAccess(false)
      setLoading(false)
    }
  }

  const fetchModelData = async () => {
    try {
      setLoading(true)

      const numericModelId = parseInt(modelId!, 10)
      if (isNaN(numericModelId)) {
        throw new Error('Invalid model ID')
      }

      // Fetch model info
      const info = await ModelInfoCacheService.getModelInfo(numericModelId)
      if (!info) {
        throw new Error('Model not found')
      }
      setModelInfo(info)
    } catch (err: any) {
      setError(err.message || 'Failed to load market data')
    } finally {
      setLoading(false)
    }
  }

  if (checkingAccess || loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-lg text-gray-600">
            {checkingAccess ? 'Checking permissions...' : 'Loading market configuration...'}
          </div>
        </div>
      </Layout>
    )
  }

  if (!hasAccess || error || !modelInfo) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-lg text-red-600 mb-4">
              {error || 'You do not have permission to access this page'}
            </div>
            <button
              onClick={() => navigate(`/market/${modelId}`)}
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              ← Back to Market
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="w-full px-2 sm:px-4 lg:px-6 xl:px-8 py-6">
          <div className="w-full max-w-full">
            <div className="mb-6">
              <button
                onClick={() => navigate(`/market/${modelId}`)}
                className="text-sm text-gray-600 hover:text-gray-900 mb-4 inline-flex items-center"
              >
                ← Back to Market
              </button>
              <h1 className="text-3xl font-bold text-purple-950">
                Configure Market: {capitalizeMakeName(modelInfo.make_name)} {formatModelName(modelInfo.model_name)}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Customize settings and options for this market
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <OptionsSection modelId={modelId!} />
              <MarketSettingsSection modelId={modelId!} />
              <EMPSection modelId={modelId!} />
              <CustomColumnsSection modelId={modelId!} />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}
